import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from 'contexts/AuthContext';

import { SetPasswordInner } from 'pages/SetPassword/SetPasswordInner';
import { Loading } from 'components/Loading';
import { Logo } from 'components/Logo';

import { LoginBottomHalfStyle, LoginStyle, LoginTopHalfStyle, LoginWrapperStyle } from 'styles/pages/login.styles';
import { TotemLoadingStyle } from 'styles/pages/totem.styles';
import { SetPasswordProps, VerifyUser } from 'pages/SetPassword/SetPassword.types';
import type { AuthContextType } from 'contexts/AuthContext';
import { useAuthenticateUserResetMutation } from 'data/__generated__';

export const ResetPassword: React.FC<SetPasswordProps> = ({ setSkip }) => {
    const { token } = useParams<{ token?: string }>();
    const {
        authActions: { setPassword: storeTokens },
    } = useContext(AuthContext) as AuthContextType;
    const [verifyUser, setVerifyUser] = useState<VerifyUser | null>(null);
    const [authenticateUserReset] = useAuthenticateUserResetMutation();

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) return;
            const result = await authenticateUserReset({ variables: { token } }).catch((e: any) => {
                if (e.message === 'GraphQL error: invalid token') {
                    setVerifyUser({ success: false, error: 'Veuillez vérifier votre lien' });
                } else if (e.message === 'GraphQL error: maxAge exceeded') {
                    setVerifyUser({
                        success: false,
                        error: 'Ce lien a expiré. Veuillez en demander un nouveau',
                    });
                } else {
                    setVerifyUser({
                        success: false,
                        error: 'Il y a eu un problème avec la réinitialisation de votre mot de passe.',
                    });
                }
            });
            if (result && !verifyUser) {
                const {
                    data: { authenticateUserReset },
                } = result as {
                    data: { authenticateUserReset: VerifyUser };
                };
                setVerifyUser(authenticateUserReset);
            }
        };
        if (!verifyUser) {
            verifyToken();
        }
    }, [verifyUser, token, authenticateUserReset]);

    if (!verifyUser) {
        return (
            <TotemLoadingStyle>
                <Loading />
            </TotemLoadingStyle>
        );
    }

    return (
        <LoginStyle>
            <LoginTopHalfStyle>
                <Logo dark />
            </LoginTopHalfStyle>
            <LoginBottomHalfStyle>
                <LoginWrapperStyle>
                    <SetPasswordInner
                        isResetPassword
                        verifyUser={verifyUser}
                        setSkip={setSkip}
                        setPassword={async (response, cb) => {
                            if (response.token && response.refreshToken) {
                                await storeTokens(response.token, response.refreshToken);
                            }
                            cb?.();
                        }}
                        token={token ?? ''}
                    />
                </LoginWrapperStyle>
            </LoginBottomHalfStyle>
        </LoginStyle>
    );
};
